import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { useMetricsTagFacetCounts } from '@/features/metrics/hooks/metrics';
import { getChartTitle } from '@/shared/copy/charts';
import {
  metricThresholdGroupKeys,
  type MetricThresholdGroupKey,
} from '@/shared/utils/metricsThresholds';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import {
  chartStatusDarkenCoefficient,
  lightModeChartStatusColors,
  uiTokens,
} from '@/theme/uiTokens';
import { CHART_EMPTY_STATE_FILTER_HINT } from '@/ui/composites';
import { useDeferredAsyncViewState } from '@/ui/composites/useDeferredAsyncViewState';
import { Box } from '@/ui/primitives';
import { darken } from '@/ui/primitives/colorUtils';
import { useTheme } from '@/ui/primitives/hooks';
import { formatStatusLabel } from '@/ui/primitives/statusChips';
import { MetricsPageToolbarPanel } from '../metricsToolbar/MetricsPageToolbar';
import { normaliseMetricsToolbarTagOptions } from '../metricsToolbar/metricsToolbarTagOptions';
import { resolveDatePreview } from '../tableSystem/tableToolbar.helpers';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';
import { MetricTrendCardsRow } from './MetricTrendCardsRow';
import { useStackedMetricsStatusChartSectionLayout } from './StackedMetricsStatusChartSection.layout';
import {
  StatusPieBreakdownPanel,
  StatusStackedChartPanel,
} from './StackedMetricsStatusChartSection.panels';
import {
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.states';
import { StatusMetricCardsGrid } from './StatusMetricCardsGrid';
import { StatusPieLegend } from './StatusPieLegend';
import {
  type MetricsStatusChartPanelModelToolbarState,
  type MetricsStatusChartPanelDisplayModel,
  useMetricsStatusChartPanelModel,
} from './useMetricsStatusChartPanelModel';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ExecutionStatusKey, MetricsScope } from '@/features/metrics/metricsHelpers';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { MetricThresholdGroup } from '@flowqa/shared';

const darkenColor = darken as (color: string, coefficient: number) => string;
const chartStatusColors = new Set(Object.values(uiTokens.statusColors));
const toTitleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z]/g, (match) => match.toUpperCase());

const resolveMetricCardCount = (card: MetricCardItem | undefined): number | null => {
  if (card?.isMissing === true) return 0;
  const count = card?.count;
  if (typeof count === 'number' && Number.isFinite(count)) return count;
  if (typeof count !== 'string') return null;
  const parsedCount = Number.parseInt(count.replaceAll(',', ''), 10);
  return Number.isFinite(parsedCount) ? parsedCount : null;
};

const resolveMetricCardSecondaryLabel = (card: MetricCardItem | undefined): string | null =>
  typeof card?.secondaryValue === 'string' && card.secondaryValue.trim().length > 0
    ? card.secondaryValue.trim()
    : null;

const isMetricCardCountPlaceholder = (card: MetricCardItem | undefined): boolean =>
  typeof card?.count === 'string' && card.count.trim() === '-';

const resolveStatusChartDataDateRange = (
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'],
): { from: Dayjs | null; to: Dayjs | null } => {
  if (!statusChartData) return { from: null, to: null };

  const dates = statusChartData.data
    .filter((datum) =>
      statusChartData.series.some((seriesItem) => {
        const value = datum[seriesItem.key];
        return typeof value === 'number' && Number.isFinite(value) && value > 0;
      }),
    )
    .map((datum) => {
      const dateValue = typeof datum.date === 'string' ? datum.date : String(datum.label);
      return dayjs(dateValue).startOf('day');
    })
    .filter((date) => date.isValid());

  if (dates.length === 0) return { from: null, to: null };

  return {
    from: dates.reduce((earliest, date) => (date.isBefore(earliest) ? date : earliest)),
    to: dates.reduce((latest, date) => (date.isAfter(latest) ? date : latest)),
  };
};
const formatLegendStatusLabel = (key: string, fallback: string): string => {
  const normalisedKey = key.toLowerCase() as Parameters<typeof formatStatusLabel>[0];
  const formattedLabel = formatStatusLabel(normalisedKey);
  return formattedLabel.length > 0
    ? formattedLabel
    : toTitleCase(fallback.length > 0 ? fallback : key);
};
const metricTrendComparisonDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});
const metricsTagFacetScopes = new Set<MetricsScope>(['suite', 'test', 'pipeline', 'executionRun']);

export type StackedMetricsStatusChartSectionProps<TStatus extends string> = {
  title?: string;
  searchAriaLabel: string;
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  tagFacetEntityId?: string | null;
  availableTags: MetricsToolbarTagOption[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel?: string;
  metricCards: MetricCardItem[];
  loading?: boolean;
  loadingDelayMs?: number;
  knownEmpty?: boolean;
  statusCardSkeletonCount?: number;
  selectedStatusIds?: string[];
  onStatusToggle?: (statusId: string) => void;
  toolbarState?: MetricsStatusChartPanelModelToolbarState<TStatus>;
  modelOverride?: MetricsStatusChartPanelDisplayModel;
  suppressAnimation: boolean;
  showPoints?: boolean;
  showSearch?: boolean;
  showToolbar?: boolean;
  includeLatestQuickFilter?: boolean;
  dateQuickFilterOptions?: DateQuickFilter[];
  showCustomDateRange?: boolean;
  showToolbarDateControls?: boolean;
  toolbarRightSlot?: ReactNode;
  showAllStatusXAxisLabels?: boolean;
  showFlakeScoreMiniMetric?: boolean;
  metricThresholdsOverride?: MetricThresholdGroup;
  showMetricThresholdSettingsLink?: boolean;
  visibleMetricKeys?: MetricThresholdGroupKey[];
};
export const StackedMetricsStatusChartSection = function StackedMetricsStatusChartSection<
  TStatus extends string,
>({
  title = getChartTitle('statusTimeline'),
  searchAriaLabel,
  noRowsBaseText,
  scope,
  projectId,
  tagFacetEntityId,
  availableTags,
  statusOptions,
  statusFilterLabel = getChartTitle('status'),
  metricCards,
  loading = false,
  loadingDelayMs,
  knownEmpty = false,
  statusCardSkeletonCount,
  selectedStatusIds: _selectedStatusIds,
  onStatusToggle: _onStatusToggle,
  toolbarState,
  modelOverride,
  suppressAnimation,
  showPoints: _showPoints = false,
  showSearch = true,
  showToolbar = true,
  includeLatestQuickFilter = false,
  dateQuickFilterOptions,
  showCustomDateRange = true,
  showToolbarDateControls = true,
  toolbarRightSlot,
  showAllStatusXAxisLabels = true,
  showFlakeScoreMiniMetric = true,
  metricThresholdsOverride,
  showMetricThresholdSettingsLink = true,
  visibleMetricKeys,
}: StackedMetricsStatusChartSectionProps<TStatus>): ReactElement {
  const miniMetricEmptyTitle = noRowsBaseText;
  const {
    piePanelHeight,
    pieChartVisualHeight,
    statusChartVisualHeight,
    statusPanel,
    miniPanels,
    sectionLayout,
  } = useStackedMetricsStatusChartSectionLayout();
  const theme = useTheme();

  const internalModel = useMetricsStatusChartPanelModel<TStatus>({
    noRowsBaseText,
    scope,
    projectId,
    externalToolbarState: toolbarState,
    disabled: modelOverride !== undefined,
  });
  const model = modelOverride ?? internalModel;
  const effectiveMetricThresholds =
    metricThresholdsOverride ?? model.metricThresholds ?? internalModel.metricThresholds;
  const effectiveMetricThresholdSettingsHref =
    model.metricThresholdSettingsHref ?? internalModel.metricThresholdSettingsHref;
  const effectiveToolbarState = internalModel.toolbarState;
  const chartAreaViewState = useDeferredAsyncViewState({
    loading: !knownEmpty && (loading || model.showStatusChartSkeleton),
    delayMs: loadingDelayMs,
  });
  const showChartAreaLoading =
    chartAreaViewState.showPlaceholder || chartAreaViewState.showSkeleton;
  const statusChartData = model.statusChartData;
  const countedStatusOptions = useMemo<Array<TableStatusOption<TStatus>>>(() => {
    return statusOptions.map((option) => ({
      ...option,
      count: option.count ?? model.facetCounts.statusCounts[option.value],
    }));
  }, [model.facetCounts.statusCounts, statusOptions]);
  const hasCustomMetricDateRange =
    effectiveToolbarState.fromDate !== null || effectiveToolbarState.toDate !== null;
  const isLatestOnlyMetricMode =
    effectiveToolbarState.dateQuickFilter === 'latest' &&
    effectiveToolbarState.fromDate === null &&
    effectiveToolbarState.toDate === null;
  const selectedMetricEntityId =
    effectiveToolbarState.selectedEntities?.length === 1
      ? (effectiveToolbarState.selectedEntities[0] ?? null)
      : null;
  const metricTagFacetEntityId = selectedMetricEntityId ?? tagFacetEntityId ?? null;
  const selectedMetricEntityIds =
    (effectiveToolbarState.selectedEntities?.length ?? 0) > 1
      ? (effectiveToolbarState.selectedEntities ?? [])
      : [];
  const availableTagOptions = useMemo<TableDropdownOption[]>(
    () => normaliseMetricsToolbarTagOptions(availableTags),
    [availableTags],
  );
  const availableTagValues = useMemo<string[]>(
    () => availableTagOptions.map((option) => option.value),
    [availableTagOptions],
  );
  const hasProvidedTagCounts = useMemo(
    () => availableTagOptions.every((option) => option.count !== undefined),
    [availableTagOptions],
  );
  const enableMetricsTagFacetCounts =
    !hasProvidedTagCounts && (modelOverride === undefined || metricsTagFacetScopes.has(scope));
  const tagFacetCounts = useMetricsTagFacetCounts({
    range:
      hasCustomMetricDateRange || isLatestOnlyMetricMode
        ? 'all'
        : effectiveToolbarState.dateQuickFilter,
    statuses: effectiveToolbarState.selectedStatuses as ExecutionStatusKey[],
    suiteIds: scope === 'test' ? effectiveToolbarState.selectedSuites : undefined,
    scope,
    projectId,
    entityId: metricTagFacetEntityId,
    entityIds: selectedMetricEntityIds,
    name: effectiveToolbarState.search.trim().length > 0 ? effectiveToolbarState.search : null,
    startDay: hasCustomMetricDateRange
      ? (effectiveToolbarState.fromDate?.format('YYYY-MM-DD') ?? null)
      : null,
    endDay: hasCustomMetricDateRange
      ? (effectiveToolbarState.toDate?.format('YYYY-MM-DD') ?? null)
      : null,
    latestOnly: isLatestOnlyMetricMode,
    tags: availableTagValues,
    enabled:
      enableMetricsTagFacetCounts &&
      availableTagValues.length > 0 &&
      (modelOverride === undefined || metricTagFacetEntityId !== null),
  });
  const countedToolbarState = effectiveToolbarState;
  const countedAvailableTags = useMemo<TableDropdownOption[]>(
    () =>
      availableTagOptions.map((option) => ({
        ...option,
        count: option.count ?? tagFacetCounts[option.value],
      })),
    [availableTagOptions, tagFacetCounts],
  );
  const statusPieData = useMemo(() => {
    if (!statusChartData) return [];
    const statusColors = isLightTheme(theme) ? lightModeChartStatusColors : uiTokens.statusColors;
    return statusChartData.series
      .map((seriesItem) => {
        const total = statusChartData.data.reduce((sum, datum) => {
          const value = datum[seriesItem.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        const explicitColor = typeof seriesItem.color === 'string' ? seriesItem.color : undefined;
        const colorKey = typeof seriesItem.colorKey === 'string' ? seriesItem.colorKey : undefined;
        const indexedColor = colorKey
          ? statusColors[colorKey as keyof typeof statusColors]
          : undefined;
        const tokenColor = typeof indexedColor === 'string' ? indexedColor : undefined;
        const baseColor = explicitColor ?? tokenColor;
        const resolvedColor =
          typeof baseColor === 'string'
            ? isLightTheme(theme)
              ? baseColor
              : darkenColor(
                  baseColor,
                  chartStatusColors.has(baseColor) ? chartStatusDarkenCoefficient : 0.26,
                )
            : undefined;
        return {
          id: seriesItem.key,
          name: formatLegendStatusLabel(seriesItem.key, seriesItem.label),
          value: total,
          color: resolvedColor,
        };
      })
      .filter((entry) => entry.value > 0);
  }, [statusChartData, theme]);
  const metricTrendRangeLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = countedToolbarState;
    if (fromDate || toDate) return 'Custom';
    if (dateQuickFilter === '7d') return '7D';
    if (dateQuickFilter === '14d') return '14D';
    if (dateQuickFilter === '30d') return '30D';
    return 'Latest';
  }, [countedToolbarState]);
  const metricTrendDeltaComparisonLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = countedToolbarState;
    if (fromDate || toDate) {
      const rawStart = (fromDate ?? toDate)?.startOf('day') ?? null;
      const rawEnd = (toDate ?? fromDate)?.startOf('day') ?? null;
      if (!rawStart || !rawEnd) return 'vs previous period';

      const start = rawStart.isAfter(rawEnd) ? rawEnd : rawStart;
      const end = rawStart.isAfter(rawEnd) ? rawStart : rawEnd;
      const spanDays = end.diff(start, 'day') + 1;
      const previousEnd = start.subtract(1, 'day');
      const previousStart = previousEnd.subtract(spanDays - 1, 'day');
      return `vs ${metricTrendComparisonDateFormatter.format(previousStart.toDate())} - ${metricTrendComparisonDateFormatter.format(previousEnd.toDate())}`;
    }
    if (dateQuickFilter === '7d') return 'vs previous 7 days';
    if (dateQuickFilter === '14d') return 'vs previous 14 days';
    if (dateQuickFilter === '30d') return 'vs previous 30 days';
    return 'vs previous period';
  }, [countedToolbarState]);
  const totalMetricCard = metricCards.find((metricCard) => metricCard.id === 'total');
  const toolbarSummaryCount = resolveMetricCardCount(totalMetricCard);
  const toolbarSummaryRelatedEntityLabel = resolveMetricCardSecondaryLabel(totalMetricCard);
  const showToolbarSummaryLoading =
    (!knownEmpty && (loading || model.showStatusChartSkeleton || showChartAreaLoading)) ||
    isMetricCardCountPlaceholder(totalMetricCard);
  const toolbarSummaryDateRange = useMemo(() => {
    const latestQueryStartDay = countedToolbarState.latestQueryStartDay ?? null;
    const latestQueryEndDay = countedToolbarState.latestQueryEndDay ?? null;
    const latestChartDateRange =
      countedToolbarState.dateQuickFilter === 'latest'
        ? resolveStatusChartDataDateRange(statusChartData)
        : { from: null, to: null };
    const latestPreviewStartDay =
      latestChartDateRange.from ??
      (countedToolbarState.dateQuickFilter === 'latest' && latestQueryStartDay
        ? dayjs(latestQueryStartDay)
        : null);
    const latestPreviewEndDay =
      latestChartDateRange.to ??
      (countedToolbarState.dateQuickFilter === 'latest' && latestQueryEndDay
        ? dayjs(latestQueryEndDay)
        : null);
    return resolveDatePreview(
      countedToolbarState.dateQuickFilter,
      countedToolbarState.fromDate,
      countedToolbarState.toDate,
      latestPreviewStartDay,
      latestPreviewEndDay,
    );
  }, [countedToolbarState, statusChartData]);
  const statusPieTotal = statusPieData.reduce((sum, entry) => sum + entry.value, 0);
  const statusLegend =
    statusPieData.length > 0 ? (
      <StatusPieLegend entries={statusPieData} total={statusPieTotal} />
    ) : null;

  const effectiveVisibleMetricKeys =
    visibleMetricKeys ?? model.visibleMetricKeys ?? metricThresholdGroupKeys;
  const visibleMiniMetricKeys = effectiveVisibleMetricKeys.filter(
    (key) => showFlakeScoreMiniMetric || key !== 'flakeScore',
  );
  const hasVisibleMiniMetrics = visibleMiniMetricKeys.length > 0;
  const visibleMiniMetricCount = Math.max(1, visibleMiniMetricKeys.length);
  const visibleMiniMetricCardHeight = Math.max(
    metricTrendCardCompactLayout.minHeight,
    (miniPanels.columnHeight - miniPanels.rowGapPx * (visibleMiniMetricCount - 1)) /
      visibleMiniMetricCount,
  );
  const visibleMiniPanels = useMemo(
    () => ({
      ...miniPanels,
      height: visibleMiniMetricCardHeight,
    }),
    [miniPanels, visibleMiniMetricCardHeight],
  );
  const statusEmptyTitle = effectiveToolbarState.noRowsText;

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'hidden',
        pb: sectionLayout.bottomGap,
      }}
    >
      {showToolbar ? (
        <Box
          sx={{
            width: '100%',
            mb: sectionLayout.toolbarGap,
          }}
        >
          <MetricsPageToolbarPanel<TStatus>
            title=""
            headerRightSlot={toolbarRightSlot}
            showInlineFilters
            showDateControls={showToolbarDateControls}
            searchAriaLabel={searchAriaLabel}
            showSearch={showSearch}
            searchValue={countedToolbarState.search}
            panelPadding="sm"
            panelPaddingX={s('sm')}
            onDebouncedSearchChange={countedToolbarState.setSearch}
            filterStatuses={countedToolbarState.selectedStatuses}
            statusOptions={countedStatusOptions}
            onFilterStatusesChange={countedToolbarState.setSelectedStatuses}
            filterEntities={countedToolbarState.selectedEntities}
            onFilterEntitiesChange={countedToolbarState.setSelectedEntities}
            entityOptions={countedToolbarState.entityOptions}
            entityLabel={countedToolbarState.entityLabel}
            filterItems={countedToolbarState.selectedItems}
            onFilterItemsChange={countedToolbarState.setSelectedItems}
            itemOptions={countedToolbarState.itemOptions}
            itemLabel={countedToolbarState.itemLabel}
            filterSuites={countedToolbarState.selectedSuites}
            onFilterSuitesChange={countedToolbarState.setSelectedSuites}
            suiteOptions={countedToolbarState.suiteOptions}
            suiteLabel={countedToolbarState.suiteLabel}
            filterTags={countedToolbarState.selectedTags}
            onFilterTagsChange={countedToolbarState.setSelectedTags}
            filterProjects={countedToolbarState.selectedProjects}
            onFilterProjectsChange={countedToolbarState.setSelectedProjects}
            projectOptions={countedToolbarState.projectOptions}
            dateQuickFilter={countedToolbarState.dateQuickFilter}
            onDateQuickFilterChange={countedToolbarState.onDateQuickFilterChange}
            fromDate={countedToolbarState.fromDate}
            toDate={countedToolbarState.toDate}
            latestQueryStartDay={countedToolbarState.latestQueryStartDay}
            latestQueryEndDay={countedToolbarState.latestQueryEndDay}
            onFromDateChange={(next) => {
              countedToolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
            }}
            onToDateChange={(next) => {
              countedToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
            }}
            availableTags={countedAvailableTags}
            includeLatestQuickFilter={includeLatestQuickFilter}
            dateQuickFilterOptions={dateQuickFilterOptions}
            showCustomDateRange={showCustomDateRange}
            statusFilterLabel={statusFilterLabel}
            headerTitlePaddingLeft={s('sm')}
            headerControlsPaddingRight={s('sm')}
            summaryCount={toolbarSummaryCount}
            summaryStartDay={
              toolbarSummaryDateRange.displayedFromDate?.format('YYYY-MM-DD') ?? null
            }
            summaryEndDay={toolbarSummaryDateRange.displayedToDate?.format('YYYY-MM-DD') ?? null}
            summaryRelatedEntityLabel={toolbarSummaryRelatedEntityLabel}
            summaryEmptyText={`${statusEmptyTitle} ${
              showSearch ? CHART_EMPTY_STATE_FILTER_HINT : 'Try adjusting filters.'
            }`}
            summaryLoading={showToolbarSummaryLoading}
          />
        </Box>
      ) : null}
      <Box sx={{ mb: sectionLayout.toolbarGap }}>
        <StatusMetricCardsGrid
          metricCards={metricCards}
          loading={knownEmpty ? false : showChartAreaLoading}
          skeletonCount={statusCardSkeletonCount}
          comparisonWindowLabel={isLatestOnlyMetricMode ? null : metricTrendDeltaComparisonLabel}
          statusChartData={model.statusChartData}
          resolveStatusCardIcon={(statusId) => resolveStackedStatusCardIcon(statusId, scope)}
          resolveStatusCardIconColor={resolveStackedStatusCardIconColor}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateAreas: {
            xs: hasVisibleMiniMetrics ? '"pie" "status" "mini"' : '"pie" "status"',
            sm: hasVisibleMiniMetrics ? '"pie status" "mini mini"' : '"pie status"',
            lg: hasVisibleMiniMetrics ? '"pie status mini"' : '"pie status"',
          },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(260px, 1.4fr) minmax(0, 2.8fr)',
            lg: hasVisibleMiniMetrics
              ? 'minmax(260px, 1.8fr) minmax(0, 4.8fr) minmax(260px, 1.9fr)'
              : 'minmax(260px, 1.8fr) minmax(0, 6.7fr)',
          },
          columnGap: sectionLayout.gap,
          rowGap: sectionLayout.rowGap,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridArea: 'pie', minWidth: 0 }}>
          <StatusPieBreakdownPanel
            loading={showChartAreaLoading}
            showPlaceholder={false}
            showErrorState={model.showChartErrorState}
            piePanelHeight={piePanelHeight}
            pieChartVisualHeight={pieChartVisualHeight}
            miniPanels={visibleMiniPanels}
            headerToChartGap={statusPanel.headerToChartGap}
            miniMetricEmptyTitle={miniMetricEmptyTitle}
            statusPieData={statusPieData}
            statusLegend={statusLegend}
            legendMarginTop={s('none')}
          />
        </Box>
        <Box sx={{ gridArea: 'status', minWidth: 0 }}>
          <StatusStackedChartPanel<TStatus>
            title={title}
            searchAriaLabel={searchAriaLabel}
            statusOptions={countedStatusOptions}
            statusFilterLabel={statusFilterLabel}
            availableTags={countedAvailableTags}
            loading={showChartAreaLoading}
            showPlaceholder={false}
            toolbarState={countedToolbarState}
            statusPanel={statusPanel}
            statusChartVisualHeight={statusChartVisualHeight}
            legendData={statusPieData}
            legendLoading={showChartAreaLoading}
            statusEmptyTitle={statusEmptyTitle}
            statusChartData={statusChartData}
            showStatusChartSkeleton={knownEmpty ? false : model.showStatusChartSkeleton}
            showChartErrorState={model.showChartErrorState}
            suppressAnimation={suppressAnimation}
            showAllXAxisLabels={showAllStatusXAxisLabels}
          />
        </Box>
        {hasVisibleMiniMetrics ? (
          <Box sx={{ gridArea: 'mini', minWidth: 0 }}>
            <MetricTrendCardsRow
              loading={showChartAreaLoading}
              metricCards={metricCards}
              rangeLabel={metricTrendRangeLabel}
              deltaComparisonLabel={metricTrendDeltaComparisonLabel}
              variant="stacked"
              includeTotalCard={false}
              showFlakeScore={showFlakeScoreMiniMetric}
              stackedCardHeight={visibleMiniPanels.height}
              visibleMetricKeys={effectiveVisibleMetricKeys}
              metricThresholds={effectiveMetricThresholds}
              metricThresholdSettingsHref={
                showMetricThresholdSettingsLink ? effectiveMetricThresholdSettingsHref : undefined
              }
              thresholdScope={scope}
              statusChartData={model.statusChartData}
              passRateChartData={model.passRateChartData}
              frequencyChartData={model.frequencyChartData}
              durationChartData={model.durationChartData}
              flakeScoreChartData={model.flakeScoreChartData}
              passRateSummary={model.passRateSummary}
              frequencySummary={model.frequencySummary}
              durationSummary={model.durationSummary}
              flakeScoreSummary={model.flakeScoreSummary}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
